import { useState } from 'react';
import { FormProvider, useForm, type SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { Divider, Form, useToast } from '@knack/asterisk-react';
import { t as tFunction } from 'i18next';
import { z } from 'zod';

import { type KnackField, type ValidationRule } from '@/types/schema/KnackField';
import { type KnackObject } from '@/types/schema/KnackObject';
import { useFieldMutation } from '@/hooks/api/mutations/useFieldMutation';
import { CriteriaForm } from '@/components/data-table/toolkit-sidebar/rules/CriteriaForm';
import { RuleFormHeader } from '@/components/data-table/toolkit-sidebar/rules/RuleFormHeader';
import { getCompatibleFields } from '@/components/data-table/toolkit-sidebar/rules/utils';
import { DiscardRuleDialog } from '@/components/data-table/toolkit-sidebar/rules/validation-rules/DiscardRuleDialog';
import { FormFooter } from '@/components/data-table/toolkit-sidebar/rules/validation-rules/form-sections/FormFooter';
import { FormHeader } from '@/components/data-table/toolkit-sidebar/rules/validation-rules/form-sections/FormHeader';
import { FormMessage } from '@/components/data-table/toolkit-sidebar/rules/validation-rules/form-sections/FormMessage';
import { FormTargetField } from '@/components/data-table/toolkit-sidebar/rules/validation-rules/form-sections/FormTargetField';
import { validationRuleMap } from '@/components/data-table/toolkit-sidebar/rules/validation-rules/utils';

const validationRuleFormSchema = z.object({
  targetFieldKey: z.string(),
  key: z.string().optional(),
  criteria: z.array(
    z.object({
      field: z.string().min(1),
      operator: z.string(),
      value: z.union([z.string(), z.boolean(), z.number(), z.array(z.string())])
    })
  ),

  message: z
    .string()
    .min(
      1,
      tFunction('components.data_table.right_sidebar.validation_rules.errors.missing_message')
    ),
  value: z.string()
});

export type ValidationRuleFormSchema = z.infer<typeof validationRuleFormSchema>;

export function ValidationRuleForm({
  table,
  setShouldRenderForm,
  ruleToEdit,
  ruleToDuplicate,
  fieldKey,
  setRuleToDuplicate
}: {
  table: KnackObject;
  setShouldRenderForm: React.Dispatch<React.SetStateAction<boolean>>;
  ruleToEdit?: ValidationRule;
  ruleToDuplicate?: ValidationRule;
  fieldKey?: string;
  setRuleToDuplicate?: React.Dispatch<
    React.SetStateAction<{ rule: ValidationRule; fieldKey: string } | null>
  >;
}) {
  const [t] = useTranslation();
  const { presentToast } = useToast();
  const { mutate: updateField } = useFieldMutation();
  const [showModal, setShowModal] = useState(false);
  const rule = ruleToEdit ?? ruleToDuplicate;

  const firstField = getCompatibleFields(table.fields)[0];

  const defaultCriteria = {
    field: firstField.key,
    operator: validationRuleMap[firstField.type].operators[0],
    value: ''
  };

  let defaultValues: ValidationRuleFormSchema = {
    targetFieldKey: '',
    criteria: [defaultCriteria],
    message: t('components.data_table.right_sidebar.validation_rules.text_area_default_value'),
    value: ''
  };

  if (rule && fieldKey) {
    defaultValues = {
      targetFieldKey: fieldKey,
      ...rule
    } as ValidationRuleFormSchema;
  }

  const formMethods = useForm<ValidationRuleFormSchema>({
    resolver: zodResolver(validationRuleFormSchema),
    defaultValues
  });

  const {
    formState: { isDirty },
    watch,
    handleSubmit
  } = formMethods;

  const isTargetFieldSelected = !!watch('targetFieldKey');

  const handleCancel = () => (isDirty ? setShowModal(true) : setShouldRenderForm(false));

  const onFormSubmit: SubmitHandler<ValidationRuleFormSchema> = (formValues) => {
    const { targetFieldKey, ...validationRuleFormValues } = formValues;

    const field = table.fields.find((f) => f.key === targetFieldKey);

    if (!field || !field.validation) return;

    const largestKey = Math.max(
      0,
      ...(field.validation.map((v: ValidationRule) => Number(v.key)) || [])
    );
    const newKey = largestKey + 1;

    const updatedField = {
      ...field,
      validation: !ruleToEdit
        ? [
            ...field.validation,
            {
              ...validationRuleFormValues,
              key: newKey.toString(),
              value: ''
            }
          ]
        : field.validation.map((v: ValidationRule) => {
            if (v.key === ruleToEdit?.key) {
              return { ...ruleToEdit, ...validationRuleFormValues };
            }
            return v;
          })
    };
    updateField(
      {
        tableKey: table.key,
        updatedField: updatedField as KnackField // TODO: FE-1944 iteratively refine type while working on additional field types
      },
      {
        onSuccess: () => {
          presentToast({
            title: t('components.data_table.right_sidebar.rule_saved')
          });
          setShouldRenderForm(false);

          if (setRuleToDuplicate) {
            setRuleToDuplicate(null);
          }
        },
        onError: () => {
          presentToast({
            title: t('components.data_table.right_sidebar.rule_save_error')
          });
        }
      }
    );
  };

  const getDataTestId = () => {
    if (!rule) {
      return 'validation-rule-form';
    }
    if (ruleToEdit) {
      return 'validation-rule-edit-form';
    }
    return 'validation-rule-duplicate-form';
  };

  return (
    <>
      <div className="space-y-4 rounded-lg bg-muted p-4">
        <FormHeader />
        <FormProvider {...formMethods}>
          <Form
            data-testid={getDataTestId()}
            id="validation-rules-form"
            onSubmit={handleSubmit(onFormSubmit)}
          >
            {!rule && <FormTargetField fieldKey={fieldKey} fields={table.fields} />}
            {isTargetFieldSelected && (
              <>
                {rule ? (
                  <RuleFormHeader fieldKey={fieldKey} table={table} />
                ) : (
                  <Divider className="my-4" />
                )}
                <CriteriaForm
                  table={table}
                  defaultValues={defaultValues}
                  defaultCriteria={defaultCriteria}
                  setShouldRenderForm={setShouldRenderForm}
                />
                <FormMessage />
              </>
            )}
            <FormFooter isTargetFieldSelected={isTargetFieldSelected} handleCancel={handleCancel} />
          </Form>
        </FormProvider>
      </div>
      <DiscardRuleDialog
        showModal={showModal}
        setShowModal={setShowModal}
        setShouldRenderForm={setShouldRenderForm}
      />
    </>
  );
}
